import axios from "axios";

import {
  extractMessages,
  extractNotifications,
  getHomepageVisitsFromPages,
  getNumericValue,
  getTodayVisits,
  getTotalVisitsFromByDate,
} from "./siteActivityFormatters";

import {
  HOME_PAGE_ALIASES,
} from "./siteActivityConfig";

/* ==========================================================================
   HELPERS HTTP
   ========================================================================== */

function isCanceledError(error) {
  return (
    error?.name === "CanceledError" ||
    error?.code === "ERR_CANCELED"
  );
}

function shouldRetry(error) {
  if (isCanceledError(error)) {
    return false;
  }

  const status = error?.response?.status;

  if (!status) {
    return true;
  }

  return [
    408,
    425,
    429,
    500,
    502,
    503,
    504,
  ].includes(status);
}

function wait(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    let finished = false;

    const cleanup = () => {
      if (signal) {
        signal.removeEventListener("abort", onAbort);
      }
    };

    const timeout = setTimeout(() => {
      if (finished) return;

      finished = true;
      cleanup();
      resolve();
    }, milliseconds);

    const onAbort = () => {
      if (finished) return;

      finished = true;
      clearTimeout(timeout);
      cleanup();

      const error = new Error("Request canceled");
      error.name = "CanceledError";

      reject(error);
    };

    if (!signal) {
      return;
    }

    if (signal.aborted) {
      onAbort();
      return;
    }

    signal.addEventListener("abort", onAbort, {
      once: true,
    });
  });
}

async function safeGetWithRetry(
  url,
  signal,
  {
    timeout = 10000,
    retries = 2,
  } = {},
) {
  let attempt = 0;
  let lastError = null;

  while (attempt <= retries) {
    try {
      const response = await axios.get(url, {
        signal,
        timeout,
      });

      return response.data;
    } catch (error) {
      lastError = error;

      if (isCanceledError(error)) {
        throw error;
      }

      if (
        attempt >= retries ||
        !shouldRetry(error)
      ) {
        throw error;
      }

      const delay =
        350 * Math.pow(2, attempt);

      await wait(delay, signal);

      attempt += 1;
    }
  }

  throw lastError;
}

function buildUrl(apiUrl, path) {
  const cleanBase =
    String(apiUrl || "").replace(/\/$/, "");

  const cleanPath =
    String(path || "").startsWith("/")
      ? path
      : `/${path}`;

  return `${cleanBase}${cleanPath}`;
}

/* ==========================================================================
   CONCURRENCE
   ========================================================================== */

export async function runWithConcurrency(
  items,
  limit,
  handler,
) {
  const safeItems =
    Array.isArray(items)
      ? items
      : [];

  if (!safeItems.length) {
    return [];
  }

  const results =
    new Array(safeItems.length);

  let cursor = 0;

  async function worker() {
    while (cursor < safeItems.length) {
      const index = cursor;

      cursor += 1;

      results[index] =
        await handler(
          safeItems[index],
          index,
        );
    }
  }

  const workerCount =
    Math.min(
      Math.max(Number(limit) || 1, 1),
      safeItems.length,
    );

  const workers =
    Array.from(
      { length: workerCount },
      () => worker(),
    );

  await Promise.all(workers);

  return results;
}

/* ==========================================================================
   FETCH GÉNÉRIQUE
   ========================================================================== */

async function fetchEntries(
  target,
  entries,
  signal,
  {
    concurrency = 1,
    timeout = 10000,
    retries = 1,
  } = {},
) {
  return runWithConcurrency(
    entries,
    concurrency,
    async ([key, path]) => {
      try {
        const data =
          await safeGetWithRetry(
            buildUrl(
              target.apiUrl,
              path,
            ),
            signal,
            {
              timeout,
              retries,
            },
          );

        return {
          key,
          ok: true,
          data,
          error: null,
        };
      } catch (error) {
        if (isCanceledError(error)) {
          throw error;
        }

        return {
          key,
          ok: false,
          data: null,

          error: {
            status:
              error?.response?.status ??
              null,

            message:
              error?.message ||
              "Erreur inconnue",

            network:
              !error?.response,
          },
        };
      }
    },
  );
}

/* ==========================================================================
   SURVEILLANCE CRITIQUE
   ========================================================================== */

export async function fetchSiteCriticalTarget(
  target,
  dateStr,
  signal,
) {
  const entries =
    Object.entries(
      target.criticalPaths || {},
    );

  if (!entries.length) {
    return {
      id: target.id,

      notifications: [],
      messages: [],

      criticalErrors: [],
      criticalSuccessKeys: [],

      monitorStatus: "stats_only",

      criticalDataStale: false,

      lastCriticalSync:
        new Date().toISOString(),
    };
  }

  const responses =
    await fetchEntries(
      target,
      entries,
      signal,
      {
        concurrency: 1,
        timeout: 10000,
        retries: 2,
      },
    );

  const result = {
    id: target.id,

    notifications: [],
    messages: [],

    criticalErrors: [],
    criticalSuccessKeys: [],

    monitorStatus: "ok",

    criticalDataStale: false,

    lastCriticalSync:
      new Date().toISOString(),
  };

  for (const response of responses) {
    if (!response.ok) {
      result.criticalErrors.push({
        key: response.key,
        ...response.error,
      });

      continue;
    }

    result.criticalSuccessKeys.push(
      response.key,
    );

    switch (response.key) {
      case "notifications": {
        /*
         * NOTIFICATIONS :
         * uniquement celles du jour sélectionné
         * et uniquement celles qui sont encore non lues.
         *
         * Elles servent ici à visualiser l'activité quotidienne.
         */
        result.notifications =
          extractNotifications(
            response.data,
            dateStr,
            {
              onlyToday: true,
              onlyUnread: true,
            },
          );

        break;
      }

      case "messages": {
        /*
         * MESSAGES :
         * tous les messages non lus.
         *
         * Pas de filtre sur la date car un message client
         * d'hier qui n'a pas été traité doit rester visible.
         */
        result.messages =
          extractMessages(
            response.data,
            dateStr,
            {
              mode: "unread",
            },
          );

        break;
      }

      default:
        break;
    }
  }

  if (
    result.criticalErrors.length === 0
  ) {
    result.monitorStatus = "ok";

    return result;
  }

  const allFailed =
    result.criticalSuccessKeys.length === 0;

  const allNetworkErrors =
    result.criticalErrors.every(
      (item) =>
        item.network === true,
    );

  if (
    allFailed &&
    allNetworkErrors
  ) {
    result.monitorStatus =
      "offline";
  } else {
    result.monitorStatus =
      "warning";
  }

  return result;
}

/* ==========================================================================
   STATISTIQUES
   ========================================================================== */

export async function fetchSiteMetricsTarget(
  target,
  dateStr,
  signal,
) {
  const entries =
    Object.entries(
      target.metricPaths || {},
    );

  const result = {
    id: target.id,

    visitsToday: null,
    visitsTotal: null,

    totalUsers: null,
    todayUsers: null,

    adherents: null,
    inscrits: null,

    metricErrors: [],

    metricsLoaded: true,
    metricsDate: dateStr,

    lastMetricsSync:
      new Date().toISOString(),
  };

  if (!entries.length) {
    return result;
  }

  const responses =
    await fetchEntries(
      target,
      entries,
      signal,
      {
        concurrency: 2,
        timeout: 10000,
        retries: 1,
      },
    );

  let homepageVisits = null;

  for (const response of responses) {
    if (!response.ok) {
      result.metricErrors.push({
        key: response.key,
        ...response.error,
      });

      continue;
    }

    const data = response.data;

    switch (response.key) {
      case "visitsByDate": {
        result.visitsToday =
          getTodayVisits(
            data,
            dateStr,
          );

        const total =
          getTotalVisitsFromByDate(
            data,
          );

        if (
          typeof total === "number"
        ) {
          result.visitsTotal = total;
        }

        break;
      }

      case "pageVisitsToday": {
        homepageVisits =
          getHomepageVisitsFromPages(
            data,
            HOME_PAGE_ALIASES,
          );

        break;
      }

      case "visitsTotal": {
        result.visitsTotal =
          getNumericValue(data);

        break;
      }

      case "totalUsers": {
        result.totalUsers =
          getNumericValue(data);

        break;
      }

      case "todayUsers": {
        result.todayUsers =
          getNumericValue(data);

        break;
      }

      case "adherents": {
        result.adherents =
          getNumericValue(data);

        break;
      }

      case "inscrits": {
        result.inscrits =
          getNumericValue(data);

        break;
      }

      default:
        break;
    }
  }

  if (
    typeof homepageVisits === "number"
  ) {
    result.visitsToday =
      homepageVisits;
  }

  return result;
}

/* ==========================================================================
   REQUÊTES CLIENTS
   ========================================================================== */

function normalizeStatus(value = "") {
  return String(value)
    .trim()
    .toLowerCase();
}

function isPendingRequest(request) {
  return (
    normalizeStatus(
      request?.status,
    ) === "pending"
  );
}

export function buildRequestsStats(
  requests = [],
) {
  const byAssociationId =
    new Map();

  const byAssociationName =
    new Map();

  const pendingRequests =
    Array.isArray(requests)
      ? requests.filter(
          isPendingRequest,
        )
      : [];

  const addToMap = (
    map,
    key,
    request,
  ) => {
    if (!key) {
      return;
    }

    const current =
      map.get(key) || {
        items: [],
        total: 0,
        pending: 0,
        high: 0,
      };

    current.items.push(request);

    current.total += 1;
    current.pending += 1;

    if (
      normalizeStatus(
        request.priority,
      ) === "high"
    ) {
      current.high += 1;
    }

    map.set(key, current);
  };

  pendingRequests.forEach(
    (request) => {
      const associationId =
        request.association_id ||
        request.associationId ||
        request.Associations?.id ||
        request.association?.id;

      const associationName =
        request.Associations
          ?.nom_asso ||
        request.association
          ?.nom_asso ||
        request.association_name;

      addToMap(
        byAssociationId,
        String(
          associationId || "",
        ),
        request,
      );

      addToMap(
        byAssociationName,
        String(
          associationName || "",
        ),
        request,
      );
    },
  );

  return {
    byAssociationId,
    byAssociationName,
  };
}

export function attachRequestsStatsToRows(
  rows,
  requestsStats,
) {
  return rows.map((row) => {
    const byId =
      requestsStats.byAssociationId.get(
        String(row.associationId),
      );

    const byName =
      requestsStats.byAssociationName.get(
        String(row.name),
      );

    const stats =
      byId || byName;

    if (!stats) {
      return {
        ...row,

        requests: [],
        requestsTotal: 0,
        requestsPending: 0,
        requestsHigh: 0,

        requestsInProgress: 0,
        requestsCompleted: 0,
      };
    }

    return {
      ...row,

      requests: stats.items,

      requestsTotal:
        stats.total,

      requestsPending:
        stats.pending,

      requestsHigh:
        stats.high,

      requestsInProgress: 0,
      requestsCompleted: 0,
    };
  });
}